import { ColDef } from 'ag-grid-community';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CustomColDef } from '@shared/components/ui/AgGridTable';
import { IncidentMapping } from '../models/Mapping';
import { Badge } from '@shared/components/ui/badge';
import { Button } from '@shared/components/ui/button';
import { Shop } from '@shared/components/ui/shop';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen } from '@fortawesome/pro-light-svg-icons';
import { useCouriers } from './useMappings';

interface UseMappingsAgGridColumnsProps {
  selectedColumns?: string[];
  onRowClick?: (mapping: IncidentMapping) => void;
}

export const useMappingsAgGridColumns = ({ selectedColumns = [], onRowClick }: UseMappingsAgGridColumnsProps) => {
  const { t } = useTranslation('mappingsModule');
  const navigate = useNavigate();
  const { data: couriers = [], isLoading: isLoadingCouriers } = useCouriers();

  const allColumnDefs: ColDef[] = useMemo(() => {
    // Crear el mapa dentro del useMemo para que esté disponible en el closure
    const couriersMapForColumns = new Map<number, { id: number; name: string; image_hash?: string | null }>();
    couriers.forEach((courier) => {
      if (courier.id && courier.name) {
        couriersMapForColumns.set(courier.id, courier);
      }
    });
    const baseColumns: CustomColDef[] = [
      {
        field: 'id',
        headerName: t('table.id'),
        sortable: false,
        minWidth: 80,
        cellRenderer: (params: any) => {
          const mapping = params.data as IncidentMapping;
          return <span className="text-sm font-medium">{mapping.id ?? '-'}</span>;
        },
      },
      {
        field: 'courier',
        headerName: t('table.courier'),
        sortable: false,
        minWidth: 200,
        valueGetter: (params) => {
          const mapping = params.data as IncidentMapping;
          if (mapping.courier?.name) {
            return mapping.courier.name;
          }
          // Buscar el courier en el mapa por courier_id (solo si courier_id es válido)
          if (mapping.courier_id && mapping.courier_id > 0) {
            const courier = couriersMapForColumns.get(mapping.courier_id);
            return courier?.name || '';
          }
          return '';
        },
        cellRenderer: (params: any) => {
          const mapping = params.data as IncidentMapping;

          // Si ya tenemos información del courier en el mapping, usarla
          if (mapping.courier?.name) {
            return <Shop name={mapping.courier.name} src={mapping.courier.image_hash || undefined} />;
          }

          // Buscar el courier en el mapa por courier_id (solo si courier_id es válido y mayor que 0)
          if (mapping.courier_id && mapping.courier_id > 0) {
            const courier = couriersMapForColumns.get(mapping.courier_id);
            if (courier?.name) {
              return <Shop name={courier.name} src={courier.image_hash || undefined} />;
            }
          }

          // Si no encontramos el courier, mostrar guión
          return <span className="text-sm text-complementary-text">-</span>;
        },
      },
      {
        field: 'external_type',
        headerName: t('table.external_type'),
        sortable: false,
        minWidth: 250,
        cellRenderer: (params: any) => {
          const mapping = params.data as IncidentMapping;
          return mapping.courier_incident_kind ? (
            <div className="flex flex-col gap-1">
              <span className="text-sm font-medium text-title">{mapping.courier_incident_kind.external_label}</span>
              {mapping.courier_incident_kind.external_code && (
                <Badge variant="outline" className="w-fit text-[10px] leading-none !py-1 bg-card-border text-text">
                  {mapping.courier_incident_kind.external_code}
                </Badge>
              )}
            </div>
          ) : (
            <span className="text-sm text-complementary-text">-</span>
          );
        },
      },
      {
        field: 'internal_type',
        headerName: t('table.internal_type'),
        sortable: false,
        minWidth: 250,
        cellRenderer: (params: any) => {
          const mapping = params.data as IncidentMapping;
          return mapping.incident_type ? (
            <div className="flex flex-col gap-1">
              <span className="text-sm font-medium">{mapping.incident_type.name || mapping.incident_type.label}</span>
              {mapping.incident_type.code && (
                <Badge variant="outline" className="w-fit text-[10px] leading-none !py-1 bg-card-border text-text">
                  {mapping.incident_type.code}
                </Badge>
              )}
            </div>
          ) : (
            <Badge variant="outline" className="text-xs bg-bg-error text-error">
              {t('form.none')}
            </Badge>
          );
        },
      },
      {
        field: 'status',
        headerName: t('table.status'),
        sortable: false,
        minWidth: 150,
        valueGetter: (params) => params.data.status,
        cellRenderer: (params: any) => {
          const mapping = params.data as IncidentMapping;
          const isMapped = mapping.status === 'mapped';
          return (
            <Badge
              className={`text-xs ${
                isMapped
                  ? 'bg-card-border light:bg-badge-bg-status-3 text-badge-status-3'
                  : 'bg-card-border light:bg-badge-bg-status-1 text-badge-status-1'
              }`}
            >
              {isMapped ? t('table.mapped') : t('table.unmapped')}
            </Badge>
          );
        },
      },
    ];

    // 🎯 COLUMNA: Acciones (siempre presente, fija a la derecha)
    baseColumns.push({
      field: 'actions',
      headerName: '',
      width: 50,
      minWidth: 50,
      maxWidth: 50,
      resizable: false,
      pinned: 'right',
      lockPosition: 'right',
      suppressMovable: true,
      suppressSizeToFit: true,
      suppressAutoSize: true,
      sortable: false,
      cellRenderer: (params: any) => {
        const mapping = params.data as IncidentMapping;
        return (
          <div className="flex justify-end w-full">
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                if (onRowClick) {
                  onRowClick(mapping);
                } else {
                  navigate(`/configuration/incident-mappings/${mapping.id}/edit`);
                }
              }}
              title={t('table.edit', 'Editar')}
              data-testid="edit-mapping-button"
              className="h-8 w-8 p-0"
            >
              <FontAwesomeIcon icon={faPen} className="w-3.5 h-3.5" />
            </Button>
          </div>
        );
      },
    });

    return baseColumns;
  }, [t, navigate, onRowClick, couriers]);

  const filteredColumnDefs = useMemo(() => {
    // 🎯 Columnas siempre visibles: ID
    const idColumn = allColumnDefs.find((col) => col.field === 'id');
    const actionsColumn = allColumnDefs.find((col) => col.field === 'actions');
    const alwaysVisible = [idColumn].filter(Boolean) as ColDef[];

    const selectableColumns = allColumnDefs.filter((column) => {
      const field = column.field || column.headerName || 'unknown';
      return field !== 'id' && field !== 'actions';
    });

    if (!selectedColumns.length) {
      // 🎯 Sin columnas seleccionadas: siempre visibles primero (ID), luego todas las demás, y acciones al final
      const result = [...alwaysVisible, ...selectableColumns];
      if (actionsColumn) {
        result.push(actionsColumn);
      }
      return result.reduce<ColDef[]>((acc, column) => {
        if (!column) return acc;
        const field = column.field || column.headerName || 'unknown';
        if (!acc.some((existing) => (existing.field || existing.headerName || 'unknown') === field)) {
          acc.push(column);
        }
        return acc;
      }, []);
    }

    // 🎯 Con columnas seleccionadas: siempre visibles primero (ID), luego las seleccionadas, y acciones al final
    const selectedDefs = selectableColumns.filter((column) => {
      const field = column.field || column.headerName || 'unknown';
      return selectedColumns.includes(field);
    });

    // 🎯 Combinar: primero siempre visibles (ID), luego seleccionadas, y acciones al final
    const combined = [...alwaysVisible];
    selectedDefs.forEach((column) => {
      const field = column.field || column.headerName || 'unknown';
      if (!combined.some((existing) => (existing.field || existing.headerName || 'unknown') === field)) {
        combined.push(column);
      }
    });

    // 🎯 Agregar columna de acciones al final (fija a la derecha)
    if (actionsColumn) {
      const field = actionsColumn.field || actionsColumn.headerName || 'unknown';
      if (!combined.some((existing) => (existing.field || existing.headerName || 'unknown') === field)) {
        combined.push(actionsColumn);
      }
    }

    return combined;
  }, [allColumnDefs, selectedColumns]);

  return {
    allColumnDefs,
    filteredColumnDefs,
    couriersLoaded: !isLoadingCouriers && couriers.length > 0,
  };
};
